import { RefObject, useEffect, useRef } from 'react';
import { findFocusableElement } from 'v5/platform/dom/isFocusable';
import type { EditorPresence } from './exclusiveEdit';
import { useExclusiveEdit } from './ExclusiveEditContext';

export interface ExclusiveEditorOptions {
  /** Names the draft in the discard prompt. */
  title: string;
  isEditing: boolean;
  isDirty: boolean;
  isSaving: boolean;
  /** Leaves edit mode without saving. */
  discard(): void;
  /**
   * The editor's root: hiding a node around it (collapse) hands it over, and
   * keeping its draft returns focus to where the user was inside it.
   */
  elementRef: RefObject<HTMLElement>;
}

export interface ExclusiveEditorControls {
  /** Enters edit mode through `open` once the editor in edit mode has let go. */
  requestEdit(open: () => void): void;
  /** Leaves edit mode without saving, asking first when the draft is dirty. */
  requestLeave(): void;
}

/**
 * Takes part in the session of the nearest `ExclusiveEditProvider` while
 * `isEditing`: reports whether the draft is dirty and lets the session
 * discard it when another editor takes over.
 */
export function useExclusiveEditor({
  title,
  isEditing,
  isDirty,
  isSaving,
  discard,
  elementRef
}: ExclusiveEditorOptions): ExclusiveEditorControls {
  const exclusiveEdit = useExclusiveEdit();
  const latest = useRef({ title, discard });
  latest.current = { title, discard };
  const presenceRef = useRef<EditorPresence | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Declared before the editor's own effects, so the field it focuses on
  // entering edit mode is already seen.
  useEffect(() => {
    if (!isEditing) return;
    const rememberFocus = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        elementRef.current?.contains(target)
      ) {
        lastFocusedRef.current = target;
      }
    };
    document.addEventListener('focusin', rememberFocus);
    return () => {
      document.removeEventListener('focusin', rememberFocus);
      lastFocusedRef.current = null;
    };
  }, [isEditing, elementRef]);

  useEffect(() => {
    if (!isEditing) return;
    const presence = exclusiveEdit.enter({
      get title() {
        return latest.current.title;
      },
      discard: () => latest.current.discard(),
      focus: () => {
        const element = elementRef.current;
        if (!element) return;
        const last = lastFocusedRef.current;
        if (last && element.contains(last)) last.focus();
        else findFocusableElement(element)?.focus();
      },
      isWithin: node => {
        const element = elementRef.current;
        return element !== null && node.contains(element);
      }
    });
    presenceRef.current = presence;
    return () => {
      presenceRef.current = null;
      presence.leave();
    };
  }, [isEditing, exclusiveEdit, elementRef]);

  useEffect(() => {
    presenceRef.current?.setDirty(isDirty);
  }, [isDirty, isEditing]);

  useEffect(() => {
    presenceRef.current?.setSaving(isSaving);
  }, [isSaving, isEditing]);

  return {
    requestEdit: exclusiveEdit.requestEdit,
    requestLeave: () => presenceRef.current?.requestLeave()
  };
}
